import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './AboutPage.module.css';

const IMG = '/static/images/about';

const layoutAreas = `
  topbar
  main
  footer
`;

// ---------- Icons (use original Webflow SVGs) ----------

const PinIcon = () => (
  <img src={`${IMG}/icon-pin.svg`} alt="" className={css.pinIcon} width={14} height={14} />
);

const HelpIcon = ({ name, alt }) => (
  <img src={`${IMG}/icon-${name}.svg`} alt={alt} className={css.helpIcon} width={32} height={32} />
);

// ---------- Stats data ----------

const STATS = [
  { number: '150k', label: 'Skydivers Worldwide' },
  { number: '50%', label: 'Of Skydivers buy gear each year' },
  { number: '40%', label: 'Of skydiving gear purchases are used' },
  { number: '35%', label: 'Have experienced fraud' },
];

// ---------- Schema ----------

function buildPageSchema(rootURL) {
  const organization = {
    '@type': 'Organization',
    '@id': `${rootURL}#organization`,
    name: 'Avento Gear GmbH',
    alternateName: 'Avento',
    description:
      'Marketplace for buying and selling used skydiving gear safely, with escrow payments, verified listings, and sport-specific search filters.',
    url: rootURL,
    logo: {
      '@type': 'ImageObject',
      url: `${rootURL}/static/icons/avento-logo.png`,
    },
    foundingDate: '2025',
    foundingLocation: {
      '@type': 'Place',
      name: 'St. Gallen, Switzerland',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Unterstrasse 6',
      addressLocality: 'St. Gallen',
      addressRegion: 'SG',
      postalCode: '9000',
      addressCountry: 'CH',
    },
    sameAs: [
      'https://www.instagram.com/aventogear/',
      'https://www.youtube.com/channel/UCfxkwuZV6U2PZcZtrXHwSlg',
      'https://whatsapp.com/channel/0029Vb694ou2f3ELVP7QrK0T',
    ],
    founder: [
      {
        '@type': 'Person',
        name: 'Lean Stein',
        jobTitle: 'Co-Founder',
        description: 'Business Development | Sales | Community',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'St. Gallen',
          addressCountry: 'Switzerland',
        },
        email: 'lean@aventogear.com',
        alumniOf: 'Bachelor in Economics',
        image: {
          '@type': 'ImageObject',
          url: `${rootURL}/static/images/about/lean.webp`,
        },
      },
      {
        '@type': 'Person',
        name: 'Tim Vietze',
        jobTitle: 'Co-Founder',
        description: 'Product Development | Tech | Marketing',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'St. Gallen',
          addressCountry: 'Switzerland',
        },
        email: 'tim@aventogear.com',
        alumniOf: [
          'Bachelor in Business Administration',
          'Master in Business Innovation',
        ],
        image: {
          '@type': 'ImageObject',
          url: `${rootURL}/static/images/about/tim.webp`,
        },
      },
    ],
    knowsAbout: [
      'Skydiving',
      'Skydiving Gear',
      'Parachute Equipment',
      'Used Skydiving Gear Marketplace',
      'Escrow Payments',
      'Skydiving Community',
    ],
    image: {
      '@type': 'ImageObject',
      url: `${rootURL}/static/images/about/team.webp`,
      caption: 'Avento founders Lean and Tim',
    },
  };

  return {
    '@type': 'AboutPage',
    name: 'About Avento | Who we are',
    url: `${rootURL}/about`,
    description:
      'The team and story behind Avento, the marketplace for used skydiving gear.',
    mainEntity: organization,
    about: organization,
  };
}

// ---------- Stats section with Intersection Observer ----------

const StatsBar = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={css.statsBar}>
      <div className={css.statsGrid} ref={ref}>
        {STATS.map((s, i) => (
          <div
            key={i}
            className={`${css.statCard} ${visible ? css.statCardVisible : ''}`}
          >
            <div className={css.statNumber}>{s.number}</div>
            <p className={css.statLabel}>{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Main component ----------

const AboutPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  const rootURL = config.marketplaceRootURL || '';
  const schemaTitle = 'About Avento | Who we are';
  const schemaDescription =
    'Avento was started in Switzerland in 2025 to make buying and selling used skydiving gear as easy, safe and fast as it should be. Learn our story, mission, and values. Buy and sell used skydiving gear securely.';
  const pageSchema = buildPageSchema(rootURL);

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={pageSchema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="AboutPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                {/* Hero */}
                <section className={css.hero}>
                  <img
                    src={`${IMG}/team.webp`}
                    alt=""
                    className={css.heroBgImage}
                    width={1200}
                    height={800}
                    aria-hidden="true"
                  />
                  <div className={css.heroBgOverlay} aria-hidden="true" />
                  <div className={css.heroInner}>
                    <div className={css.heroText}>
                      <h1 className={css.heroTitle}>
                        We are the marketplace for skydivers
                      </h1>
                      <p className={css.heroBody}>
                        We got tired of the way skydivers buy and sell gear.
                        Facebook posts that drown in days, no way to search
                        properly, and the constant risk of getting scammed.
                      </p>
                    </div>
                    <div className={css.heroImageWrap}>
                      <img
                        src={`${IMG}/team.webp`}
                        alt="Avento founders Lean and Tim"
                        className={css.heroImage}
                        width={1200}
                        height={800}
                        loading="eager"
                      />
                    </div>
                  </div>
                </section>

                {/* Stats */}
                <StatsBar />

                {/* Our Team */}
                <section className={css.section}>
                  <div className={css.container}>
                    <h2 className={css.sectionHeading}>Our Team</h2>
                    <p className={css.teamIntro}>
                      We started Avento because we were tired of the way
                      skydivers trade gear online. Not finding anything, scams
                      everywhere and most just sitting on their closet queen
                      because the hassle is too big.
                    </p>
                    <div className={css.teamGrid}>
                      {/* Lean */}
                      <div className={css.teamCard}>
                        <div className={css.teamCardContent}>
                          <div className={css.teamCardInner}>
                            <h3 className={css.teamName}>Lean Stein</h3>
                            <div className={css.teamMeta}>
                              <PinIcon />
                              <span>St. Gallen, Switzerland</span>
                            </div>
                            <p className={css.teamBio}>
                              Lean started jumping when he was on exchange in
                              Thailand. Now mainly in Hohenems, Austria. Next to
                              Avento and skydiving he desperately tries to
                              finally finish his bachelor in Economics. He is
                              the king of all-nighters and has a very
                              questionable sleep rythm.
                            </p>
                          </div>
                          <a
                            href="mailto:lean@aventogear.com"
                            className={css.teamEmail}
                          >
                            lean@aventogear.com
                          </a>
                        </div>
                        <div className={css.teamPhotoWrap}>
                          <img
                            src={`${IMG}/lean.webp`}
                            alt="Lean Stein - Co-Founder of Avento"
                            className={css.teamPhoto}
                            width={800}
                            height={534}
                            loading="lazy"
                          />
                        </div>
                      </div>

                      {/* Tim */}
                      <div className={css.teamCard}>
                        <div className={css.teamCardContent}>
                          <div className={css.teamCardInner}>
                            <h3 className={css.teamName}>Tim Vietze</h3>
                            <div className={css.teamMeta}>
                              <PinIcon />
                              <span>St. Gallen, Switzerland</span>
                            </div>
                            <p className={css.teamBio}>
                              Tim likes the outdoors, always being busy with
                              something and sidequests. He is doing his Masters
                              in Business Innovation next to Avento and other
                              than Lean, he likes early mornings.
                            </p>
                          </div>
                          <a
                            href="mailto:tim@aventogear.com"
                            className={css.teamEmail}
                          >
                            tim@aventogear.com
                          </a>
                        </div>
                        <div className={css.teamPhotoWrap}>
                          <img
                            src={`${IMG}/tim.webp`}
                            alt="Tim Vietze - Co-Founder of Avento"
                            className={css.teamPhoto}
                            width={800}
                            height={534}
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* How We Help */}
                <section className={`${css.section} ${css.lastSection}`}>
                  <div className={css.container}>
                    <h2 className={css.sectionHeading}>How we help you</h2>
                    <div className={css.helpGrid}>
                      {/* Buyers */}
                      <div className={css.helpCard}>
                        <h3 className={css.helpCardTitle}>Buyers</h3>
                        <div className={css.helpPoints}>
                          <div className={css.helpPoint}>
                            <div className={css.helpIconWrap}>
                              <HelpIcon name="safe" alt="Safe icon" />
                            </div>
                            <div className={css.helpPointText}>
                              <h4 className={css.helpPointTitle}>
                                Escrow Payments
                              </h4>
                              <p className={css.helpPointDesc}>
                                With our escrow payments, your money stays safe
                                in our escrow account until you have received the
                                gear and brought it to your rigger for
                                inspection.
                              </p>
                            </div>
                          </div>
                          <div className={css.helpPoint}>
                            <div className={css.helpIconWrap}>
                              <HelpIcon name="filters" alt="Filter icon" />
                            </div>
                            <div className={css.helpPointText}>
                              <h4 className={css.helpPointTitle}>
                                Specific Filters
                              </h4>
                              <p className={css.helpPointDesc}>
                                Search by canopy size, container model, jump
                                number and hundreds of other properties. Find
                                exactly what you need instead of scrolling
                                through hundreds of Facebook posts.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sellers */}
                      <div className={css.helpCard}>
                        <h3 className={css.helpCardTitle}>Sellers</h3>
                        <div className={css.helpPoints}>
                          <div className={css.helpPoint}>
                            <div className={css.helpIconWrap}>
                              <HelpIcon name="eye" alt="Eye icon" />
                            </div>
                            <div className={css.helpPointText}>
                              <h4 className={css.helpPointTitle}>
                                Visibility of Listing
                              </h4>
                              <p className={css.helpPointDesc}>
                                Your listing reaches buyers actually searching
                                for your exact gear. We promote it across our
                                network, Google Shopping, socials and ads, all
                                for free to you.
                              </p>
                            </div>
                          </div>
                          <div className={css.helpPoint}>
                            <div className={css.helpIconWrap}>
                              <HelpIcon name="handshake" alt="Handshake icon" />
                            </div>
                            <div className={css.helpPointText}>
                              <h4 className={css.helpPointTitle}>
                                Trust from Buyers
                              </h4>
                              <p className={css.helpPointDesc}>
                                Escrow payments mean buyers feel safe purchasing
                                from you, even across borders. No more
                                back-and-forth agreeing on how to wire the
                                payment.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const AboutPage = connect(mapStateToProps)(AboutPageComponent);

export default AboutPage;
